/**
 * CATÁLOGO DE MÓDULOS — fuente ÚNICA de verdad.
 *
 * Antes esta información vivía dispersa y divergente en 3 sitios: la union
 * `FeatureName`, `MODULES_INFO` (ModulesPage) y `FEATURE_LABELS`
 * (FeatureOverridesCard) — con datos que ni coincidían (p.ej. `ecommerce` figuraba
 * como plan 'pro' en un lado y estaba en 'basic' en PLAN_FEATURES). Ahora todo
 * módulo se define UNA vez aquí, y esos otros se derivan de este catálogo.
 *
 * Hay DOS ejes de visibilidad, independientes (así lo hacen Shopify/Stripe/Vercel):
 *
 *   1) MADUREZ (este archivo, `status`): ¿el módulo está terminado?
 *        development → sin terminar. Oculto a clientes; visible solo en cuentas
 *                      internas (tenants.is_internal).
 *        beta        → usable pero no anunciado. Igual que development de cara al
 *                      cliente por ahora (oculto salvo cuentas internas).
 *        stable      → listo para clientes.
 *
 *   2) ENTITLEMENT (PLAN_FEATURES, features.config.ts): ¿el plan del tenant lo
 *      incluye comercialmente?
 *
 * Un cliente ve un módulo SOLO si es `stable` Y su plan lo incluye. Las cuentas
 * internas ven todo (incluye development/beta). El filtro de madurez se aplica en
 * FeatureFlagsContext, después de resolver el plan + overrides.
 */

import type { FeatureName } from './features.config';

export type ModuleStatus = 'development' | 'beta' | 'stable';

export type ModuleCategory =
  | 'ventas'
  | 'operaciones'
  | 'clientes'
  | 'canales'
  | 'marketing'
  | 'finanzas'
  | 'ia';

export interface ModuleDef {
  key: FeatureName;
  /** Nombre visible (el que ve el usuario en menú, ajustes, catálogo). */
  name: string;
  description: string;
  category: ModuleCategory;
  /** Madurez. Solo `stable` es visible para clientes; el resto solo en cuentas internas. */
  status: ModuleStatus;
}

/**
 * El catálogo. `status` refleja la realidad de desarrollo declarada por el founder
 * (2026-07-13): lo no terminado va `development` y se oculta a clientes, pero sigue
 * visible en las cuentas internas (Sandbox, Dulti, Vitalex) para poder trabajarlo.
 */
export const MODULE_CATALOG: Record<FeatureName, ModuleDef> = {
  // ── Ventas ────────────────────────────────────────────────────────────────
  pos: {
    key: 'pos',
    name: 'Punto de venta',
    description: 'POS para tomar pedidos en tienda física',
    category: 'ventas',
    status: 'development',
  },
  kds: {
    key: 'kds',
    name: 'KDS',
    description: 'Pantalla de cocina (Kitchen Display System)',
    category: 'ventas',
    status: 'development',
  },
  ecommerce: {
    key: 'ecommerce',
    name: 'Tienda online',
    description: 'E-commerce con catálogo, checkout y dominios',
    category: 'ventas',
    status: 'development',
  },
  /*
   * Salió de adentro de `ecommerce` el 1-sep-2026. Es `stable` mientras la tienda sigue en
   * desarrollo, y eso NO es una contradicción: la bandeja de pedidos web está terminada y se
   * alimenta hoy desde Shopify, que es un canal que el cliente ya puede conectar. Lo que falta
   * terminar es la tienda propia, no la pantalla que muestra lo que entra.
   */
  marketplaces: {
    key: 'marketplaces',
    name: 'Marketplaces',
    description: 'Publicar en Mercado Libre y Falabella, sincronizar stock y recibir sus pedidos',
    category: 'ventas',
    status: 'stable',
  },
  pedidos_online: {
    key: 'pedidos_online',
    name: 'Pedidos',
    description: 'Pedidos y carritos abandonados que entran por un canal web, sea la tienda propia o Shopify',
    category: 'ventas',
    status: 'stable',
  },
  cod: {
    key: 'cod',
    name: 'Formulario COD',
    description: 'Pago contra entrega con form builder y prevención de fraude',
    category: 'ventas',
    status: 'stable',
  },
  discounts: {
    key: 'discounts',
    name: 'Descuentos',
    description: 'Cupones y códigos de descuento',
    category: 'ventas',
    status: 'development',
  },
  /*
   * Las tres de abajo separan lo que el plan Drop no usa. Hasta el 25-ago-2026 no existían porque
   * todos los planes las tenían de hecho: la distinción no hacía falta, no porque no fuera real.
   */
  catalogo_propio: {
    key: 'catalogo_propio',
    name: 'Catálogo propio',
    description: 'Variantes, existencias, ofertas y modificadores de TUS productos. Un revendedor importa el producto tal cual: no lo define ni ajusta su stock.',
    category: 'ventas',
    status: 'stable',
  },
  analiticas: {
    key: 'analiticas',
    name: 'Analíticas',
    description: 'Reportes de negocio: ranking, mapa de calor, rentabilidad, P&L y comisiones.',
    category: 'ventas',
    status: 'development',
  },
  flota_propia: {
    key: 'flota_propia',
    name: 'Flota propia',
    description: 'Motorizados y rendimiento del recaudo. Quien despacha por agencia no tiene ni una cosa ni la otra.',
    category: 'operaciones',
    status: 'stable',
  },
  // ── Operaciones ───────────────────────────────────────────────────────────
  operaciones: {
    key: 'operaciones',
    name: 'Operaciones',
    description: 'Ventas, logística y cobranzas unificadas',
    category: 'operaciones',
    status: 'stable',
  },
  logistics: {
    key: 'logistics',
    name: 'Logística propia',
    description: 'Agencias, motorizados y delivery interno',
    category: 'operaciones',
    status: 'stable',
  },
  proveedores: {
    key: 'proveedores',
    name: 'Proveedores externos',
    description: 'Catálogos de Dropi y Aliclik para importar y despachar',
    category: 'operaciones',
    status: 'stable',
  },
  inventory: {
    key: 'inventory',
    name: 'Inventario avanzado',
    description: 'Control de stock por sucursal y multi-almacén',
    category: 'operaciones',
    status: 'stable',
  },
  // ── Clientes ──────────────────────────────────────────────────────────────
  crm: {
    key: 'crm',
    name: 'CRM',
    description: 'Contactos, pipeline, tareas, agenda y secuencias',
    category: 'clientes',
    status: 'stable',
  },
  /*
   * Nació el 1-sep-2026 saliendo de adentro de `crm`, que encendía las dos entradas de menú a la
   * vez. Ver el comentario en `FeatureName` (features.config.ts) para el porqué.
   *
   * 🪤 ARRANCA EN `development` A PROPÓSITO. No es un módulo nuevo sin construir: son 10 pantallas
   *    que ya existen y funcionan para las cuentas internas. Lo que no está listo es venderlo — no
   *    tiene una sola prueba automatizada y obliga a conectar telefonía antes de que sirva de algo.
   *    Pasarlo a `stable` es un UPDATE en `platform_module_status`, sin desplegar.
   */
  call_center: {
    key: 'call_center',
    name: 'Llamadas',
    description: 'Marcador, campañas de voz, Voice AI y coaching de llamadas',
    category: 'clientes',
    status: 'development',
  },
  /*
   * Ya existía el motor —1.241 entregas, cuatro canales— y hasta una pantalla. Lo que no existía
   * era la puerta: vivía en Ajustes llamada «Notificaciones por WhatsApp» y nadie la encontraba.
   * Nace `stable` porque no hay nada que terminar: hay que dejar de esconderlo.
   */
  avisos: {
    key: 'avisos',
    name: 'Avisos',
    description: 'Qué eventos del negocio te notifican y por dónde: vendiste, un cliente pide un asesor, se cayó una integración',
    category: 'operaciones',
    status: 'stable',
  },
  loyalty: {
    key: 'loyalty',
    name: 'Sellos',
    description: 'Tarjeta de sellos por cliente que vive en su WhatsApp: sellar en caja, premio, avisos automáticos',
    category: 'clientes',
    // 21-sep-2026 · Nace con Sellos (S1–S3). El módulo anterior (puntos + wallet) nunca tuvo un cliente: sus tablas no tenían políticas.
    status: 'stable',
  },
  // ── Canales ───────────────────────────────────────────────────────────────
  whatsapp: {
    key: 'whatsapp',
    name: 'Chats',
    description: 'Bandeja compartida de WhatsApp con equipos, menciones y respuestas rápidas',
    category: 'canales',
    status: 'stable',
  },
  chatbot: {
    key: 'chatbot',
    name: 'Chatbot IA',
    description: 'Constructor de flujos conversacionales con IA y widget de chat',
    category: 'canales',
    status: 'stable',
  },
  agents: {
    key: 'agents',
    name: 'Agentes IA',
    description: 'Studio para crear agentes y flujos conversacionales',
    category: 'canales',
    status: 'development',
  },
  copilot: {
    key: 'copilot',
    name: 'Copiloto',
    description: 'Agente operativo IA: consulta y ejecuta acciones en todo el negocio',
    category: 'canales',
    status: 'development',
  },
  // ── Marketing ─────────────────────────────────────────────────────────────
  marketing: {
    key: 'marketing',
    name: 'Marketing',
    description: 'Campañas y upselling automatizado',
    category: 'marketing',
    status: 'development',
  },
  ads: {
    key: 'ads',
    name: 'Smarty Ads',
    description: 'Campañas de Meta Ads gestionadas con IA',
    category: 'marketing',
    status: 'development',
  },
  // ── Finanzas ──────────────────────────────────────────────────────────────
  finance: {
    key: 'finance',
    name: 'Control de Caja',
    description: 'Gestión financiera y reportes contables',
    category: 'finanzas',
    status: 'stable',
  },
  accounting: {
    key: 'accounting',
    name: 'Contabilidad',
    description: 'Comprobantes electrónicos SUNAT (CPE) y reportes contables',
    category: 'finanzas',
    status: 'development',
  },
  // ── IA ────────────────────────────────────────────────────────────────────
  commander: {
    key: 'commander',
    name: 'IA & Copilot',
    description: 'Asistente IA y chatbots conversacionales',
    category: 'ia',
    status: 'stable',
  },
};

/** Nombre visible de un módulo (reemplaza los labels duplicados). */
export function moduleName(key: FeatureName): string {
  return MODULE_CATALOG[key]?.name ?? key;
}

/** ¿El módulo está listo para clientes (stable)? Los no-stable solo se ven en cuentas internas. */
export function isModuleStable(key: FeatureName): boolean {
  return MODULE_CATALOG[key]?.status === 'stable';
}

/** Claves cuyo módulo NO es stable (development/beta) — las que el filtro de madurez oculta a no-internos. */
export function nonStableModuleKeys(): FeatureName[] {
  return (Object.keys(MODULE_CATALOG) as FeatureName[]).filter((k) => !isModuleStable(k));
}

/**
 * Status efectivo de cada módulo = override de BD (platform_module_status) si existe,
 * sino el default del catálogo (código). Devuelve las claves cuyo status efectivo NO es
 * stable — las que el filtro de madurez oculta. Overrides con claves desconocidas o
 * status inválido se ignoran (cae al default del catálogo).
 */
export function resolveNonStableKeys(statusOverrides: Record<string, ModuleStatus>): FeatureName[] {
  return (Object.keys(MODULE_CATALOG) as FeatureName[]).filter((k) => {
    const status = statusOverrides[k] ?? MODULE_CATALOG[k].status;
    return status !== 'stable';
  });
}

/**
 * Eje de MADUREZ. Toma un mapa de features ya resuelto (plan + tenant_features +
 * overrides) y, si la cuenta NO es interna, apaga toda feature cuyo módulo no sea
 * `stable`. Las cuentas internas (is_internal) ven todo. No muta la entrada.
 *
 * `nonStableKeys` permite pasar el status EFECTIVO (override de BD vía
 * resolveNonStableKeys); por defecto usa el status del catálogo (código). Es la última
 * capa: gana para OCULTAR (nunca reenciende algo ya apagado). Vive aparte para testear.
 */
export function applyMaturityFilter(
  features: Record<string, boolean>,
  isInternal: boolean,
  nonStableKeys: FeatureName[] = nonStableModuleKeys(),
): Record<string, boolean> {
  const out = { ...features };
  if (isInternal) return out;
  for (const key of nonStableKeys) out[key] = false;
  return out;
}
